import * as React from 'react'
import { useQuery } from '@apollo/react-hooks'
import gql from 'graphql-tag'

import {
  i18n,
  useEnterpriseContext,
  useHorizonSessionSettings,
  useSiteConfig,
} from '@thg-commerce/enterprise-core'
import { HorizonSessionSettings } from '@thg-commerce/enterprise-core/src/EnterpriseContext/types'
import { RecommendationTrackingContext } from '@thg-commerce/enterprise-metrics/src/backend_event/types/tracking'
import {
  Country,
  Currency,
  Feature,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import { useTheme } from '@thg-commerce/enterprise-theme'
import { ProductRecommendations } from '@thg-commerce/enterprise-widget-product-recommendations/src/ProductRecommendations'
import { ColourChoice } from '@thg-commerce/gravity-elements/Swatch'
import {
  mq,
  spacing,
  styled,
  Text,
  TextEntry,
  TextStyle,
  TextTransform,
} from '@thg-commerce/gravity-theme'

import { useProductPage } from '../../context'

import { RecentlyViewedProductRailSkeleton } from './RecentlyViewedProductRailSkeleton'

export interface RecentlyViewedProduct {
  sku: number
  images: {
    largeProduct: string
  }[]
  title: string
  url: string
  swatch?: ColourChoice[]
  reviews: {
    averageScore: number
    total: number
  }
  price: {
    price: {
      defaultPrice: string
      minPrice: string
      maxPrice: string
    }
    rrp: {
      defaultPrice: string
      minPrice: string
      maxPrice: string
    }
  }
  inStock: boolean
}

export interface RecommendedProduct {
  url: string
  title: string
  sku: number
  image: string
  reviews: {
    averageScore: number
    total: number
  }
  brand?: {
    name: string
    imageUrl?: string
  }
  price: {
    price: {
      defaultPrice: string
      minPrice: string
      maxPrice: string
    }
    rrp: {
      defaultPrice: string
      minPrice: string
      maxPrice: string
    }
  }
  inStock: boolean
}

const Title = styled.p<{
  textEntry?: TextEntry
  textStyle?: TextStyle
  textTransform?: TextTransform
}>`
  ${(props) =>
    Text(props.textEntry || 'large1', props.textStyle || 'alternate')};
  text-transform: ${(props) => props.textTransform || 'none'};
  margin: ${spacing(2)} 0 ${spacing(4)};
  text-align: center;

  ${(props) => mq(props.theme.breakpointUtils.map, 'lg')} {
    margin: ${spacing(4)} 0;
  }
`

export const buildQuery = (numberOfProducts: number) => {
  return gql`query products (
      $strict: Boolean!
      $currency: Currency!
      $shippingDestination: Country!
      $clickAndCollectEnabled: Boolean!
      $vipPriceEnabled: Boolean! = false
      $linkedSkusEnabled: Boolean
    ) {
      ${Array(numberOfProducts)
        .fill(0)
        .map(
          (_, index) => `
        product_${index}: productBlock(
          sku: $product_${index},
          strict: $strict,
          currency: $currency,
          shippingDestination: $shippingDestination
          clickAndCollectEnabled: $clickAndCollectEnabled
          vipPriceEnabled: $vipPriceEnabled
        ) @client {
          sku
          colourSwatches
          externalIdentifier
          priceRange
          rrpRange
          images {
            largeProduct
          }
          brand {
            name
            imageUrl
          }
          title
          url
          reviews {
            total
            averageScore
          }
          price
          inStock
          defaultVariant {
            marketedSpecialOffer {
              title {
                content {
                  content
                    }
              }
              description {
                content {
                  content
                }
              }
              landingPageLink {
                text
                url
                openExternally
                noFollow
              }
            }
            externalIdentifier
            inStock
            eligibleForFulfilmentMethods @include(if: $clickAndCollectEnabled)
            isCheckStock @include(if: $clickAndCollectEnabled)
            isOrderInStore @include(if: $clickAndCollectEnabled)
            leadTime @include(if: $clickAndCollectEnabled)
            weightGroups @include(if: $clickAndCollectEnabled)
            isBookable @include(if: $clickAndCollectEnabled)
            inStockLocations @include(if: $clickAndCollectEnabled)
          }
          content {
            key
            value {
              ... on ProductContentStringValue {
                stringValue: value
              }
              ... on ProductContentStringListValue {
                stringListValue: value
              }
            }
          }
        }`,
        )
        .join('\n')}
    }`
}

export const RecentlyViewedProductRail = (props: {
  inViewport: boolean
  forwardedRef: React.Ref<any>
  enterCount: number
}) => {
  const { recentlyViewedSkus } = useProductPage()
  const sessionSettings = useHorizonSessionSettings()
  const theme = useTheme()
  const {
    showPdpLinkWhenOutOfStock,
    enableClickAndCollect,
    enableVipPrice,
    linkedSkusEnabled,
    disableSavingOnRecentlyViewed,
  } = useSiteConfig()
  const { horizonFeatures } = useEnterpriseContext()
  const vipPriceEnabled =
    (enableVipPrice && horizonFeatures?.includes(Feature.VipPricingEnabled)) ||
    false

  const showPlaceholder = !props.inViewport && props.enterCount === 0
  const skipQuery =
    showPlaceholder || !recentlyViewedSkus || recentlyViewedSkus!.size < 1

  const i18nText = {
    recentlyViewedTitle: i18n('product.recentlyviewedproducts.title'),
    rrpLabel: i18n('product.price.rrp.text'),
  }

  const queryVariables = skipQuery
    ? {}
    : getQueryVariables(
        recentlyViewedSkus!,
        sessionSettings,
        enableClickAndCollect,
        vipPriceEnabled,
        linkedSkusEnabled,
      )

  const { data, loading } = useQuery<
    { [key: string]: RecentlyViewedProduct | null },
    | {
        strict: Boolean
        currency: Currency
        shippingDestination: Country
        clickAndCollectEnabled: Boolean
        vipPriceEnabled: Boolean
      }
    | {}
  >(buildQuery(skipQuery ? 1 : recentlyViewedSkus!.size), {
    skip: skipQuery,
    variables: queryVariables,
  })

  if (showPlaceholder) {
    return <div style={{ visibility: 'hidden' }} ref={props.forwardedRef} />
  }

  if (loading) return <RecentlyViewedProductRailSkeleton />
  if (data && recentlyViewedSkus) {
    const products = getRecentlyViewedProducts(
      data,
      recentlyViewedSkus!,
      i18nText.rrpLabel,
    )

    if (!products) return null
    return (
      <div ref={props.forwardedRef}>
        <Title
          textEntry={theme.widget.productRecommendations.title.font.entry}
          textStyle={theme.widget.productRecommendations.title.font.style}
          textTransform={
            theme.widget.productRecommendations.title.font.transform
          }
        >
          {i18nText.recentlyViewedTitle}
        </Title>
        <ProductRecommendations
          products={products}
          itemsPerSlide={
            theme.widget.productRecommendations.carousel.itemsPerSlide
          }
          trackingContext={RecommendationTrackingContext.DEFAULT}
          recommendationsStyle={theme.widget.productRecommendations}
          showPdpLinkWhenOutOfStock={showPdpLinkWhenOutOfStock}
          widgetTitle={i18nText.recentlyViewedTitle}
          carouselSize={theme.widget?.productRecommendations?.carousel?.size}
          enableSavePrice={disableSavingOnRecentlyViewed}
        />
      </div>
    )
  }

  return null
}

export const getQueryVariables = (
  recentlyViewedSkus: Set<string>,
  sessionSettings: HorizonSessionSettings,
  enableClickAndCollect: boolean = false,
  vipPriceEnabled: boolean,
  linkedSkusEnabled?: boolean,
) => {
  const queryVariables = {
    ...sessionSettings,
    vipPriceEnabled,
    linkedSkusEnabled,
    strict: false,
    clickAndCollectEnabled: enableClickAndCollect,
  }
  Array.from(recentlyViewedSkus!).forEach((item, index) => {
    queryVariables[`product_${index}`] = item
  })
  return queryVariables
}

export const getRecentlyViewedProducts = (
  data,
  recentlyViewedSkus: Set<string>,
  rrpText: string,
) => {
  const productsMap = new Map<string, RecentlyViewedProduct>()

  if (data) {
    Array.from(recentlyViewedSkus!).forEach((sku: string, index) => {
      const productData = data[`product_${index}`]
      productData && productsMap.set(sku, productData)
    })
  }

  if (!productsMap || productsMap.size === 0) {
    return null
  }

  const recentlyViewedProductsData = Array.from(productsMap.values())

  return filterProductData(recentlyViewedProductsData, rrpText)
}

const filterProductData = (productData, rrpText: string) =>
  productData.reduce((result: RecommendedProduct[], data) => {
    const image = data.images?.find((currentImage) => currentImage.largeProduct)
      ?.largeProduct

    if (image) {
      result.push({
        ...data,
        image,
        price: {
          ...data.price,
          rrpText,
        },
      })
    }
    return result
  }, [])
